import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Search, Filter, ArrowLeft, Save, X, 
  Image as ImageIcon, CheckCircle2, Upload, Scale, Gem, Calculator, 
  RefreshCw, ChevronLeft, ChevronRight, Camera, PlusCircle, Clock,
  AlertCircle, WifiOff, Tag, CheckSquare, Square, Percent, Calendar
} from 'lucide-react';
import { Product, GoldRate } from '../types';
import AdminPromoModal from './AdminPromoModal';

interface AdminProductsProps {
  products: Product[];
  rates: GoldRate[];
  onAdd: (product: Product) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  onNavigate: (page: string) => void;
  onRefresh?: () => Promise<void>;
  initialView?: 'list' | 'form';
}

// Category type with custom categories
interface Category {
  id: string;
  name: string;
  value: string;
}

// Extended Product interface for local state
interface ExtendedProduct extends Product {
  priceOverridden?: boolean;
  isPreOrder?: boolean;
  preOrderReleaseDate?: string;
}

const SITE_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://sparklinggems.shop'
    : 'http://localhost/sparkling-gems-backend-php';

const PLACEHOLDER_IMAGE = 'https://placehold.co/80x80/f5f5f5/999999?text=No+Image';

// ============================================================
// FIXED: ENHANCED IMAGE RESOLVER - Properly handles all image fields
// ============================================================
const getProductImageUrl = (product: Product | any, debug: boolean = false): string => {
  // Debug first product only to avoid console spam
  if (debug && product?.id === 1) {
    console.log('🔍 DEBUG - Raw product data:', {
      id: product.id,
      name: product.name,
      image: product.image,
      image_url: (product as any).image_url,
      has_image: !!product.image,
      has_image_url: !!(product as any).image_url,
      image_url_value: (product as any).image_url?.substring(0, 100),
      all_keys: Object.keys(product)
    });
  }

  // CRITICAL FIX: Try image_url FIRST (this is what your database returns and App.tsx now includes)
  if ((product as any).image_url && typeof (product as any).image_url === 'string' && 
      (product as any).image_url.trim() !== '' && (product as any).image_url.length > 5) {
    const trimmed = (product as any).image_url.trim();
    
    if (debug && product?.id === 1) {
      console.log(`✅ Found image_url for product ${product.id}:`, trimmed.substring(0, 100));
    }
    
    // Return base64 images as-is
    if (trimmed.startsWith('data:image')) {
      return trimmed;
    }
    
    // Return absolute URLs as-is
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    
    // Handle paths with leading slash
    if (trimmed.startsWith('/')) {
      const fullUrl = `${SITE_BASE_URL}${trimmed}`;
      if (debug && product?.id === 1) console.log(`   → Full URL: ${fullUrl}`);
      return fullUrl;
    }
    
    // Handle paths without leading slash
    if (!trimmed.startsWith('http') && !trimmed.startsWith('data:')) {
      const fullUrl = `${SITE_BASE_URL}/${trimmed}`;
      if (debug && product?.id === 1) console.log(`   → Full URL: ${fullUrl}`);
      return fullUrl;
    }
    
    return trimmed;
  }

  // Priority 2: Try image field
  if (product.image && typeof product.image === 'string' && 
      product.image.trim() !== '' && product.image.length > 5) {
    const trimmed = product.image.trim();
    
    if (debug && product?.id === 1) {
      console.log(`✅ Found image field for product ${product.id}:`, trimmed.substring(0, 100));
    }
    
    if (trimmed.startsWith('data:image')) return trimmed;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;
    if (!trimmed.startsWith('http') && !trimmed.startsWith('data:')) {
      return `${SITE_BASE_URL}/${trimmed}`;
    }
    return trimmed;
  }

  // Priority 3: Try other possible image fields
  const otherFields = [
    (product as any).imageUrl,
    (product as any).mainImage,
    (product as any).main_image,
    (product as any).product_image,
    (product as any).productImage,
    (product as any).img,
    (product as any).photo,
    (product as any).picture
  ];

  for (const img of otherFields) {
    if (img && typeof img === 'string' && img.trim() !== '' && img.length > 5) {
      const trimmed = img.trim();
      if (debug && product?.id === 1) {
        console.log(`✅ Found other image field for product ${product.id}:`, trimmed.substring(0, 100));
      }
      if (trimmed.startsWith('data:image')) return trimmed;
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
      if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;
      return `${SITE_BASE_URL}/${trimmed}`;
    }
  }

  // Priority 4: Try gallery images
  const galleryImages = (product as any).galleryImages || (product as any).gallery_images;
  if (galleryImages && Array.isArray(galleryImages) && galleryImages.length > 0) {
    for (const galleryImg of galleryImages) {
      if (galleryImg && typeof galleryImg === 'string' && galleryImg.trim() !== '' && galleryImg.length > 10) {
        const trimmed = galleryImg.trim();
        if (debug && product?.id === 1) {
          console.log(`📸 Using gallery image for product ${product.id}:`, trimmed.substring(0, 100));
        }
        if (trimmed.startsWith('data:image')) return trimmed;
        if (trimmed.startsWith('http')) return trimmed;
        if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;
        return `${SITE_BASE_URL}/${trimmed}`;
      }
    }
  }

  // No image found
  if (debug && product?.id === 1) {
    console.log(`❌ No image found for product ${product.id}, using placeholder`);
  }
  return PLACEHOLDER_IMAGE;
};

// ============================================================
// Helper function to normalize image URL for form preview
// ============================================================
const normalizeImageUrl = (imageStr?: string): string => {
  if (!imageStr || typeof imageStr !== 'string') return PLACEHOLDER_IMAGE;

  const trimmed = imageStr.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;

  if (trimmed.startsWith('data:image')) return trimmed;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;

  return `${SITE_BASE_URL}/${trimmed}`;
};

const AdminProducts: React.FC<AdminProductsProps> = ({ 
  products, rates, onAdd, onEdit, onDelete, onNavigate, onRefresh, initialView = 'list' 
}) => {
  const [view, setView] = useState<'list' | 'form'>(initialView);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPromo, setFilterPromo] = useState<'all' | 'promo' | 'non-promo'>('all');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [loadingImages, setLoadingImages] = useState<Record<number, boolean>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  
  // Selection and Promo state
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  
  // Gallery images state
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  
  // Categories management
  const [categories, setCategories] = useState<Category[]>([
    { id: '1', name: 'Jewelry', value: 'jewelry' },
    { id: '2', name: 'Bags', value: 'bags' },
    { id: '3', name: 'Fragrances', value: 'fragrances' },
    { id: '4', name: 'Watches', value: 'watches' },
    { id: '5', name: 'Accessories', value: 'accessories' },
  ]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // Price override state
  const [useManualPrice, setUseManualPrice] = useState(false);
  const [manualPrice, setManualPrice] = useState<number>(0);
  
  // Pre-order state
  const [isPreOrder, setIsPreOrder] = useState(false);
  const [preOrderDate, setPreOrderDate] = useState('');
  
  // Condition options
  const conditionOptions = [
    { value: 'Brand New', label: 'Brand New', color: 'emerald', icon: '🏷️' },
    { value: 'Pre-loved', label: 'Pre-loved (Used)', color: 'amber', icon: '🔄' },
    { value: 'Vintage', label: 'Vintage', color: 'purple', icon: '📅' },
    { value: 'Like New', label: 'Like New', color: 'blue', icon: '✨' },
    { value: 'Good', label: 'Good Condition', color: 'green', icon: '👍' },
    { value: 'Fair', label: 'Fair Condition', color: 'orange', icon: '⚠️' },
  ];
  
  // Form State - using ExtendedProduct
  const [currentProduct, setCurrentProduct] = useState<Partial<ExtendedProduct>>({
    id: undefined,
    name: '',
    category: 'jewelry',
    price: 0,
    description: '',
    image: '',
    galleryImages: [],
    isNew: false,
    isBestSeller: false,
    weight: '',
    karat: '18k',
    goldType: 'Saudi Gold',
    condition: 'Brand New',
    stock: 0,
    priceOverridden: false,
    isPreOrder: false,
    preOrderReleaseDate: ''
  });

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  // Debug products when they change
  useEffect(() => {
    if (products.length > 0 && products[0]) {
      console.log('📦 Products received in AdminProducts:', products.length);
      console.log('📦 First product sample:', {
        id: products[0].id,
        name: products[0].name,
        image: products[0].image,
        image_url: (products[0] as any).image_url,
        galleryImages: (products[0] as any).galleryImages,
        all_keys: Object.keys(products[0])
      });
    }
  }, [products]);

  // Reset image errors when products change
  useEffect(() => {
    setImageErrors({});
    setLoadingImages({});
  }, [products]);

  const resetForm = () => {
    setCurrentProduct({
      id: undefined,
      name: '',
      category: 'jewelry',
      price: 0,
      description: '',
      image: '',
      galleryImages: [],
      isNew: false,
      isBestSeller: false,
      weight: '',
      karat: '18k',
      goldType: 'Saudi Gold',
      condition: 'Brand New',
      stock: 0,
      priceOverridden: false,
      isPreOrder: false,
      preOrderReleaseDate: ''
    });
    setGalleryImages([]);
    setCurrentGalleryIndex(0);
    setUseManualPrice(false);
    setManualPrice(0);
    setIsPreOrder(false);
    setPreOrderDate('');
    setApiError(null);
  };

  // Update filteredProducts to include promo filter
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    
    const isPromo = (product as any).is_promo === true;
    const matchesPromo = filterPromo === 'all' || 
                         (filterPromo === 'promo' && isPromo) ||
                         (filterPromo === 'non-promo' && !isPromo);
    
    return matchesSearch && matchesCategory && matchesPromo;
  });

  // Helper function to safely parse weight
  const parseWeight = (weight: string | number | undefined): number => {
    if (weight === undefined || weight === null) return NaN;
    const weightStr = typeof weight === 'string' ? weight : String(weight);
    return parseFloat(weightStr.replace(/[^0-9.]/g, ''));
  };

  // Calculate price based on weight and gold rate - ONLY for jewelry
  const calculatePrice = () => {
    if (useManualPrice) return manualPrice;
    
    // Only calculate for jewelry category
    if (currentProduct.category !== 'jewelry') {
      return currentProduct.price || 0;
    }
    
    const weightVal = parseWeight(currentProduct.weight);
    
    if (!isNaN(weightVal) && weightVal > 0 && currentProduct.goldType && currentProduct.karat) {
      const matchedRate = rates.find(
        r => r.goldType === currentProduct.goldType && r.karat === currentProduct.karat
      );
      
      if (matchedRate) {
        return weightVal * matchedRate.pricePerGram;
      }
    }
    return 0;
  };

  // Auto-Compute Logic for jewelry prices - ONLY for jewelry
  useEffect(() => {
    if (view === 'form' && currentProduct.category === 'jewelry' && !useManualPrice) {
      const computedPrice = calculatePrice();
      if (computedPrice > 0 && Math.abs((currentProduct.price || 0) - computedPrice) > 0.01) {
        setCurrentProduct(prev => ({ ...prev, price: computedPrice, priceOverridden: false }));
      }
    }
  }, [currentProduct.weight, currentProduct.goldType, currentProduct.karat, currentProduct.category, view, rates, useManualPrice]);

  const handleEditClick = (product: Product) => {
    const galleryImagesArray = Array.isArray(product.galleryImages) 
      ? product.galleryImages 
      : (product.galleryImages ? [product.galleryImages] : []);
    
    const extendedProduct = product as any;
    
    const isPriceOverridden = extendedProduct.priceOverridden || false;
    setUseManualPrice(isPriceOverridden);
    setManualPrice(product.price || 0);
    
    const isPreOrderProduct = extendedProduct.isPreOrder || false;
    setIsPreOrder(isPreOrderProduct);
    setPreOrderDate(extendedProduct.preOrderReleaseDate || '');
    
    setCurrentProduct({
      ...product,
      galleryImages: galleryImagesArray,
      priceOverridden: isPriceOverridden,
      isPreOrder: isPreOrderProduct,
      preOrderReleaseDate: extendedProduct.preOrderReleaseDate || ''
    });
    setGalleryImages(galleryImagesArray);
    setView('form');
    setApiError(null);
  };

  const handleAddClick = () => {
    resetForm();
    setView('form');
  };

  const handleCancel = () => {
    resetForm();
    setView('list');
  };

  const handleAddCategory = () => {
    if (newCategoryName) {
      const categoryValue = newCategoryName.toLowerCase().replace(/\s+/g, '-');
      
      const newCategory: Category = {
        id: Date.now().toString(),
        name: newCategoryName,
        value: categoryValue
      };
      setCategories([...categories, newCategory]);
      setNewCategoryName('');
      setShowAddCategory(false);
      setCurrentProduct({ ...currentProduct, category: categoryValue as any });
    }
  };

  const handleManualPriceChange = (value: number) => {
    setManualPrice(value);
    if (useManualPrice) {
      setCurrentProduct(prev => ({ ...prev, price: value, priceOverridden: true }));
    }
  };

  const applyPromoToProducts = async (promoData: any) => {
    setIsSaving(true);
    try {
      const response = await fetch('https://sparklinggems.shop/api/products/promo.php?action=apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(promoData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`Promo applied to ${data.affected_rows} products`);
        if (onRefresh) {
          await onRefresh();
        }
        setSelectedProducts([]);
        setSelectMode(false);
      } else {
        alert('Error: ' + (data.error || 'Failed to apply promo'));
      }
    } catch (error) {
      console.error('Promo error:', error);
      alert('Failed to apply promo. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageError = (productId: number) => {
    console.log(`❌ Image failed to load for product ${productId}`);
    setImageErrors(prev => ({ ...prev, [productId]: true }));
    setLoadingImages(prev => ({ ...prev, [productId]: false }));
  };

  const handleImageLoad = (productId: number) => {
    setLoadingImages(prev => ({ ...prev, [productId]: false }));
  };

  const compressImage = (base64: string, maxWidth = 800, quality = 0.7): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        
        const compressed = canvas.toDataURL('image/jpeg', quality);
        resolve(compressed);
      };
      img.onerror = () => {
        resolve(base64);
      };
    });
  };

  const handleMainImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image too large. Please use images under 5MB.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        try {
          const compressed = await compressImage(base64, 800, 0.7);
          setCurrentProduct(prev => ({ ...prev, image: compressed }));
        } catch (error) {
          setCurrentProduct(prev => ({ ...prev, image: base64 }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsSaving(true);
    
    try {
      const newImages: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (file.size > 5 * 1024 * 1024) {
          alert(`Image ${file.name} is too large. Please use images under 5MB.`);
          continue;
        }
        
        if (!file.type.startsWith('image/')) {
          alert(`File ${file.name} is not an image.`);
          continue;
        }

        const reader = new FileReader();
        
        const base64 = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
        
        const compressed = await compressImage(base64, 600, 0.6);
        newImages.push(compressed);
      }
      
      if (newImages.length > 0) {
        setGalleryImages(prev => [...prev, ...newImages]);
        setCurrentProduct(prev => ({
          ...prev,
          galleryImages: [...(prev.galleryImages || []), ...newImages]
        }));
      }
    } catch (error) {
      console.error('Error processing gallery images:', error);
      alert('Error processing images. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
    setCurrentProduct(prev => ({
      ...prev,
      galleryImages: prev.galleryImages?.filter((_, i) => i !== index)
    }));
    if (currentGalleryIndex >= index) {
      setCurrentGalleryIndex(prev => Math.max(0, prev - 1));
    }
  };

  // ============================================================
  // ENHANCED API FUNCTIONS with proper error handling
  // ============================================================
  const saveProductToDatabase = async (productData: any) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      // Log what we're sending (without full base64 for debugging)
      const logData = { ...productData };
      if (logData.image && logData.image.length > 100) {
        logData.image = `[BASE64_IMAGE: ${logData.image.length} bytes]`;
      }
      if (logData.galleryImages && logData.galleryImages.length) {
        logData.galleryImages = `[${logData.galleryImages.length} images]`;
      }
      console.log('📤 Saving product to API:', logData);
      
      const response = await fetch('https://sparklinggems.shop/api/products/create.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`HTTP error ${response.status}: ${errorText.substring(0, 200)}`);
      }
      
      const data = await response.json();
      console.log('📥 API response:', data);
      return data;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  };

  const updateProductInDatabase = async (productData: any) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      // Log what we're sending
      const logData = { ...productData };
      if (logData.image && logData.image.length > 100) {
        logData.image = `[BASE64_IMAGE: ${logData.image.length} bytes]`;
      }
      if (logData.galleryImages && logData.galleryImages.length) {
        logData.galleryImages = `[${logData.galleryImages.length} images]`;
      }
      console.log('📤 Updating product to API:', logData);
      
      const response = await fetch(`https://sparklinggems.shop/api/products/update.php?id=${productData.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`Server error: ${response.status} - ${errorText.substring(0, 200)}`);
      }
      
      const data = await response.json();
      console.log('📥 API response:', data);
      return data;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  };

  const deleteProductFromDatabase = async (productId: number) => {
    try {
      console.log('🗑️ Deleting product:', productId);
      const response = await fetch(`https://sparklinggems.shop/api/products/delete.php?id=${productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: productId })
      });
      
      const data = await response.json();
      console.log('Delete response:', data);
      return data;
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  };

  const saveLocally = (productData: any) => {
    const savedProduct: Product = {
      id: productData.id || Date.now(),
      name: productData.name,
      description: productData.description,
      price: productData.price,
      image: productData.image || '',
      galleryImages: productData.galleryImages || [],
      category: productData.category as 'jewelry' | 'bags' | 'fragrances' | 'watches' | 'accessories',
      goldType: productData.goldType,
      karat: productData.karat,
      weight: productData.weight,
      condition: productData.condition,
      isNew: productData.isNew === 1,
      isBestSeller: productData.isBestSeller === 1,
      stock: productData.stock,
      ...(isPreOrder && { isPreOrder: true, preOrderReleaseDate: preOrderDate }),
      ...(useManualPrice && { priceOverridden: true })
    };
    
    if (productData.id) {
      onEdit(savedProduct);
    } else {
      onAdd(savedProduct);
    }
    
    return { success: true, message: 'Product saved locally' };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setApiError(null);
    
    if (!currentProduct.name || !currentProduct.price || !currentProduct.category) {
      alert('Please fill in all required fields (Name, Price, Category)');
      setIsSaving(false);
      return;
    }
    
    const galleryImagesArray = Array.isArray(galleryImages) ? galleryImages : [];
    const weightValue = currentProduct.weight ? currentProduct.weight.toString() : '';
    const karatValue = currentProduct.karat && currentProduct.karat.trim() !== '' ? currentProduct.karat : '18k';
    const goldTypeValue = currentProduct.goldType && currentProduct.goldType.trim() !== '' ? currentProduct.goldType : 'Saudi Gold';
    
    const productData = {
      id: currentProduct.id,
      name: currentProduct.name,
      description: currentProduct.description || '',
      price: currentProduct.price,
      image: currentProduct.image || '',
      galleryImages: galleryImagesArray,
      category: currentProduct.category,
      goldType: goldTypeValue,
      karat: karatValue,
      weight: weightValue,
      condition: currentProduct.condition || 'Brand New',
      isNew: currentProduct.isNew ? 1 : 0,
      isBestSeller: currentProduct.isBestSeller ? 1 : 0,
      stock: currentProduct.stock || 0,
      priceOverridden: useManualPrice ? 1 : 0,
      isPreOrder: isPreOrder ? 1 : 0,
      preOrderReleaseDate: preOrderDate || null
    };
    
    try {
      let result;
      
      try {
        if (currentProduct.id) {
          result = await updateProductInDatabase(productData);
        } else {
          result = await saveProductToDatabase(productData);
        }
        
        if (result.success) {
          alert(currentProduct.id ? 'Product updated successfully!' : 'Product saved successfully!');
          
          const savedProduct: Product = {
            id: result.id || result.product?.id || currentProduct.id || Date.now(),
            name: productData.name,
            description: productData.description,
            price: productData.price,
            image: productData.image,
            galleryImages: galleryImagesArray,
            category: productData.category as any,
            goldType: productData.goldType,
            karat: productData.karat,
            weight: productData.weight,
            condition: productData.condition,
            isNew: productData.isNew === 1,
            isBestSeller: productData.isBestSeller === 1,
            stock: productData.stock,
            ...(isPreOrder && { isPreOrder: true, preOrderReleaseDate: preOrderDate }),
            ...(useManualPrice && { priceOverridden: true })
          };
          
          if (currentProduct.id) {
            onEdit(savedProduct);
          } else {
            onAdd(savedProduct);
          }
          
          if (onRefresh) {
            await onRefresh();
          }
          
          if (savedProduct.id) {
            setImageErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[savedProduct.id];
              return newErrors;
            });
          }
          
          handleCancel();
        } else {
          alert('Error: ' + (result.error || result.message || 'Unknown error'));
        }
      } catch (apiError: any) {
        console.error('API error:', apiError);
        setApiError(apiError.message || 'Connection error. Saved locally.');
        setIsOfflineMode(true);
        
        const localResult = saveLocally(productData);
        if (localResult.success) {
          alert('Product saved locally (offline mode). It will sync when connection is restored.');
          handleCancel();
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      alert('Connection error. Please check your network and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      setIsDeleting(id);
      try {
        const result = await deleteProductFromDatabase(id);
        if (result.success) {
          onDelete(id);
          if (onRefresh) {
            await onRefresh();
          }
          alert('Product deleted successfully!');
        } else {
          alert('Error: ' + (result.error || 'Failed to delete product'));
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('Connection error. Please try again.');
      } finally {
        setIsDeleting(null);
      }
    }
  };

  const getConditionIcon = (condition: string) => {
    const option = conditionOptions.find(opt => opt.value === condition);
    return option?.icon || '🏷️';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No end date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  // Track if we've logged debug info
  const [debugLogged, setDebugLogged] = useState(false);
  
  // Log debug info for first product only once
  useEffect(() => {
    if (!debugLogged && products.length > 0) {
      const firstProduct = products[0];
      if (firstProduct) {
        console.log('🔍 ========== ADMIN PRODUCTS DEBUG ==========');
        console.log('🔍 Total products:', products.length);
        console.log('🔍 First product:', {
          id: firstProduct.id,
          name: firstProduct.name,
          image: firstProduct.image,
          image_url: (firstProduct as any).image_url,
          has_image_field: 'image' in firstProduct,
          image_field_type: typeof firstProduct.image,
          image_field_value: firstProduct.image?.substring(0, 100),
          all_props: Object.keys(firstProduct)
        });
        console.log('🔍 ===========================================');
        setDebugLogged(true);
      }
    }
  }, [products, debugLogged]);

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <button 
              onClick={() => onNavigate('admin-dashboard')} 
              className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors"
            >
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
            <h1 className="font-serif text-3xl text-stone-900">Inventory Management</h1>
            
          </div>
          {view === 'list' && (
            <div className="flex gap-2">
              {selectMode ? (
                <>
                  <button
                    onClick={() => {
                      if (selectedProducts.length === filteredProducts.length) {
                        setSelectedProducts([]);
                      } else {
                        setSelectedProducts(filteredProducts.map(p => p.id));
                      }
                    }}
                    className="px-4 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors flex items-center gap-2"
                  >
                    <CheckSquare size={14} />
                    {selectedProducts.length === filteredProducts.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedProducts.length > 0 && (
                    <button
                      onClick={() => setShowPromoModal(true)}
                      className="px-4 py-3 bg-gold-600 text-white text-xs uppercase tracking-widest hover:bg-gold-700 transition-colors flex items-center gap-2"
                    >
                      <Tag size={14} /> Add Promo ({selectedProducts.length})
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectMode(false);
                      setSelectedProducts([]);
                    }}
                    className="px-4 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setSelectMode(true)}
                    className="px-4 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors flex items-center gap-2"
                  >
                    <Tag size={14} /> Add Promo
                  </button>
                  <button 
                    onClick={handleAddClick}
                    className="px-4 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg"
                  >
                    <Plus size={14} /> Add Product
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* API Error Banner */}
        {apiError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center justify-between">
            <span className="text-sm">{apiError}</span>
            <button onClick={() => setApiError(null)} className="text-red-500 hover:text-red-700">
              <X size={16} />
            </button>
          </div>
        )}

        {view === 'list' ? (
          /* LIST VIEW */
          <div className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden">
            {/* Toolbar */}
            <div className="p-6 border-b border-stone-100 flex flex-col md:flex-row gap-4 justify-between bg-stone-50/30">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <input 
                  type="text" 
                  placeholder="Search products..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none text-sm"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Tag size={16} className="text-stone-400" />
                  <select 
                    value={filterPromo}
                    onChange={(e) => setFilterPromo(e.target.value as any)}
                    className="border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none"
                  >
                    <option value="all">All Products</option>
                    <option value="promo">Promo Only</option>
                    <option value="non-promo">Non-Promo</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-stone-400" />
                  <select 
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.value}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-stone-100 text-stone-500 text-[10px] uppercase tracking-widest font-bold">
                  <tr>
                    {selectMode && <th className="px-4 py-4 w-12"></th>}
                    <th className="px-6 py-4">Image</th>
                    <th className="px-6 py-4">Product Name</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Price (₱)</th>
                    <th className="px-6 py-4">Promo</th>
                    <th className="px-6 py-4">Details</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredProducts.map((product, index) => {
                    const extendedProduct = product as any;
                    const isPromo = extendedProduct.is_promo === true;
                    const promoPrice = extendedProduct.promo_price;
                    const promoPercentage = extendedProduct.promo_percentage;
                    const promoLabel = extendedProduct.promo_label;
                    const promoEndDate = extendedProduct.promo_end_date;
                    
                    // Debug first product only
                    const isDebug = index === 0;
                    const productImageUrl = getProductImageUrl(product, isDebug);
                    const hasImage = productImageUrl !== PLACEHOLDER_IMAGE && !imageErrors[product.id];
                    
                    return (
                      <tr key={product.id} className={`hover:bg-stone-50/50 transition-colors ${isPromo ? 'bg-red-50/30' : ''}`}>
                        {selectMode && (
                          <td className="px-4 py-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProducts(prev =>
                                  prev.includes(product.id)
                                    ? prev.filter(id => id !== product.id)
                                    : [...prev, product.id]
                                );
                              }}
                              className="focus:outline-none"
                            >
                              {selectedProducts.includes(product.id) ? (
                                <CheckSquare size={18} className="text-gold-600" />
                              ) : (
                                <Square size={18} className="text-stone-400" />
                              )}
                            </button>
                            </td>
                        )}
                        <td className="px-6 py-4">
                          <div className="relative w-12 h-12 bg-stone-100 rounded-sm overflow-hidden border border-stone-200 flex items-center justify-center">
                            {loadingImages[product.id] ? (
                              <div className="w-full h-full flex items-center justify-center">
                                <RefreshCw size={16} className="animate-spin text-stone-300" />
                              </div>
                            ) : hasImage ? (
                              <img 
                                src={productImageUrl}
                                alt={product.name} 
                                className="w-full h-full object-cover"
                                onError={() => handleImageError(product.id)}
                                onLoad={() => handleImageLoad(product.id)}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-stone-100">
                                <ImageIcon size={16} className="text-stone-400 mb-1" />
                                <span className="text-[8px] text-stone-400">No Image</span>
                              </div>
                            )}
                            {isPromo && (
                              <div className="absolute inset-0 bg-red-600/10 flex items-center justify-center">
                                <span className="bg-red-600 text-white text-[8px] px-1 py-0.5 rounded-sm uppercase font-bold rotate-[-20deg] shadow-lg">
                                  PROMO
                                </span>
                              </div>
                            )}
                          </div>
                          </td>
                        <td className="px-6 py-4">
                          <span className="font-serif text-stone-900 block">{product.name}</span>
                          <span className="text-xs text-stone-400 truncate max-w-[200px] block">{product.description}</span>
                          </td>
                        <td className="px-6 py-4">
                          <span className="text-xs uppercase tracking-wider text-stone-600 bg-stone-100 px-2 py-1 rounded">{product.category}</span>
                          </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            {isPromo && promoPrice ? (
                              <>
                                <span className="text-xs text-stone-400 line-through">₱ {product.price.toLocaleString()}</span>
                                <span className="font-medium text-red-600">₱ {promoPrice.toLocaleString()}</span>
                              </>
                            ) : (
                              <span className="font-medium text-stone-900">₱ {product.price.toLocaleString()}</span>
                            )}
                          </div>
                          </td>
                        <td className="px-6 py-4">
                          {isPromo ? (
                            <div className="flex flex-col gap-1">
                              <span className="bg-red-600 text-white text-[9px] px-2 py-0.5 rounded-full w-fit flex items-center gap-1">
                                <Tag size={10} /> {promoLabel || 'SALE'}
                              </span>
                              {promoPercentage && (
                                <span className="text-[9px] text-red-600 flex items-center gap-1">
                                  <Percent size={10} /> {promoPercentage}% OFF
                                </span>
                              )}
                              {promoEndDate && (
                                <span className="text-[8px] text-stone-500 flex items-center gap-1">
                                  <Calendar size={8} /> Until {formatDate(promoEndDate)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-stone-400 italic">No promo</span>
                          )}
                          </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            {product.condition && (
                              <span className={`text-[9px] uppercase tracking-widest px-1 rounded w-fit flex items-center gap-1 ${
                                product.condition === 'Brand New' ? 'text-emerald-600 bg-emerald-50' :
                                product.condition === 'Pre-loved' ? 'text-amber-600 bg-amber-50' :
                                product.condition === 'Vintage' ? 'text-purple-600 bg-purple-50' :
                                product.condition === 'Like New' ? 'text-blue-600 bg-blue-50' :
                                product.condition === 'Good' ? 'text-green-600 bg-green-50' :
                                product.condition === 'Fair' ? 'text-orange-600 bg-orange-50' :
                                'text-stone-600 bg-stone-50'
                              }`}>
                                <span>{getConditionIcon(product.condition)}</span>
                                {product.condition === 'Pre-loved' ? 'Pre-loved' : product.condition}
                              </span>
                            )}
                            {product.isNew && <span className="text-[9px] uppercase tracking-widest text-blue-600 bg-blue-50 w-fit px-1 rounded">New</span>}
                            {product.isBestSeller && <span className="text-[9px] uppercase tracking-widest text-gold-600 bg-gold-50 w-fit px-1 rounded">Bestseller</span>}
                            {extendedProduct.isPreOrder && (
                              <span className="text-[9px] uppercase tracking-widest text-purple-600 bg-purple-50 w-fit px-1 rounded flex items-center gap-1">
                                <Clock size={10} /> Pre-Order
                              </span>
                            )}
                            {/* ONLY show jewelry-specific fields for jewelry category */}
                            {product.category === 'jewelry' && (
                              <>
                                {product.weight && <span className="text-[9px] text-stone-500">{product.weight}</span>}
                                {product.karat && <span className="text-[9px] text-stone-500">{product.karat}</span>}
                                {product.goldType && <span className="text-[9px] text-stone-500">{product.goldType}</span>}
                              </>
                            )}
                            {product.galleryImages && Array.isArray(product.galleryImages) && product.galleryImages.length > 0 && (
                              <span className="text-[9px] text-stone-500 flex items-center gap-1">
                                <Camera size={10} /> {product.galleryImages.length} photos
                              </span>
                            )}
                            {extendedProduct.priceOverridden && (
                              <span className="text-[9px] text-amber-600 bg-amber-50 w-fit px-1 rounded">Manual Price</span>
                            )}
                          </div>
                          </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button 
                              onClick={() => handleEditClick(product)}
                              className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded transition-colors"
                              title="Edit"
                              disabled={isDeleting === product.id}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button 
                              onClick={() => handleDelete(product.id)}
                              className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Delete"
                              disabled={isDeleting === product.id}
                            >
                              {isDeleting === product.id ? (
                                <RefreshCw size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                          </td>
                        </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* FORM VIEW - unchanged */
          <div className="bg-white border border-stone-200 shadow-xl rounded-sm max-w-4xl mx-auto overflow-hidden animate-fade-in">
            <div className="px-8 py-6 border-b border-stone-100 bg-stone-50 flex justify-between items-center">
              <h2 className="font-serif text-2xl text-stone-900">
                {currentProduct.id ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={handleCancel} className="text-stone-400 hover:text-stone-900">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={currentProduct.name || ''}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, name: e.target.value })}
                    className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                    required
                    placeholder="e.g., Diamond Necklace"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                    Description
                  </label>
                  <textarea
                    value={currentProduct.description || ''}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, description: e.target.value })}
                    rows={4}
                    className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none resize-none"
                    placeholder="Product description..."
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                    Category *
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={currentProduct.category || 'jewelry'}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, category: e.target.value as any })}
                      className="flex-1 border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                      required
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.value}>{cat.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowAddCategory(true)}
                      className="px-3 border border-stone-300 rounded-sm hover:bg-stone-50"
                      title="Add new category"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                  
                  {showAddCategory && (
                    <div className="mt-2 p-3 bg-stone-50 border border-stone-200 rounded-sm">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Category name"
                        className="w-full p-2 text-sm border border-stone-300 rounded-sm focus:border-gold-500 focus:outline-none mb-2"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddCategory}
                          className="px-3 py-1 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddCategory(false)}
                          className="px-3 py-1 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors rounded-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                      Price (₱) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">₱</span>
                      <input
                        type="number"
                        value={currentProduct.price || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          setCurrentProduct({ ...currentProduct, price: isNaN(value) ? 0 : value });
                          if (!useManualPrice && currentProduct.category === 'jewelry') {
                            setUseManualPrice(true);
                          }
                        }}
                        className="w-full pl-8 pr-4 py-3 border border-stone-300 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                        required
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                      Stock
                    </label>
                    <input
                      type="number"
                      value={currentProduct.stock || ''}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, stock: parseInt(e.target.value) || 0 })}
                      className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                      min="0"
                    />
                  </div>
                </div>

                {/* Pre-order Options */}
                <div className="border-t border-stone-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="isPreOrder"
                      checked={isPreOrder}
                      onChange={(e) => setIsPreOrder(e.target.checked)}
                      className="w-4 h-4 text-gold-600 rounded"
                    />
                    <label htmlFor="isPreOrder" className="text-xs uppercase tracking-widest text-stone-500 font-bold">
                      Pre-Order Item
                    </label>
                  </div>
                  
                  {isPreOrder && (
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                        Expected Release Date
                      </label>
                      <input
                        type="date"
                        value={preOrderDate}
                        onChange={(e) => setPreOrderDate(e.target.value)}
                        className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-4 pt-4 border-t border-stone-100">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={currentProduct.isNew || false}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, isNew: e.target.checked })}
                      className="w-4 h-4 text-gold-600 rounded"
                    />
                    <span className="text-sm text-stone-700">New Arrival</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={currentProduct.isBestSeller || false}
                      onChange={(e) => setCurrentProduct({ ...currentProduct, isBestSeller: e.target.checked })}
                      className="w-4 h-4 text-gold-600 rounded"
                    />
                    <span className="text-sm text-stone-700">Best Seller</span>
                  </label>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                    Condition
                  </label>
                  <select
                    value={currentProduct.condition || 'Brand New'}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, condition: e.target.value })}
                    className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                  >
                    {conditionOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {/* Jewelry Specific Fields */}
                {currentProduct.category === 'jewelry' && (
                  <>
                    <div className="bg-stone-50 p-4 border border-stone-200 rounded-sm">
                      <h3 className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2">
                        <Gem size={14} /> Jewelry Specifications
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                            Karat
                          </label>
                          <select
                            value={currentProduct.karat || '18k'}
                            onChange={(e) => setCurrentProduct({ ...currentProduct, karat: e.target.value })}
                            className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                          >
                            <option value="24k">24k</option>
                            <option value="22k">22k</option>
                            <option value="21k">21k</option>
                            <option value="18k">18k</option>
                            <option value="14k">14k</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                            Weight (g)
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={currentProduct.weight || ''}
                              onChange={(e) => setCurrentProduct({ ...currentProduct, weight: e.target.value })}
                              className="w-full pr-8 pl-3 py-3 border border-stone-300 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                              placeholder="e.g., 5.00"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">g</span>
                          </div>
                        </div>
                        
                        <div className="col-span-2">
                          <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                            Gold Type
                          </label>
                          <select
                            value={currentProduct.goldType || 'Saudi Gold'}
                            onChange={(e) => setCurrentProduct({ ...currentProduct, goldType: e.target.value })}
                            className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none"
                          >
                            <option value="Saudi Gold">Saudi Gold</option>
                            <option value="Italian Gold">Italian Gold</option>
                            <option value="Turkish Gold">Turkish Gold</option>
                            <option value="Emirati Gold">Emirati Gold</option>
                            <option value="Local PH Gold">Local PH Gold</option>
                            <option value="Ongpin Gold">Ongpin Gold</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="manualPrice"
                        checked={useManualPrice}
                        onChange={(e) => {
                          setUseManualPrice(e.target.checked);
                          if (e.target.checked) {
                            setManualPrice(currentProduct.price || 0);
                          } else {
                            setCurrentProduct(prev => ({ ...prev, priceOverridden: false }));
                          }
                        }}
                        className="w-4 h-4 text-gold-600 rounded"
                      />
                      <label htmlFor="manualPrice" className="text-xs uppercase tracking-widest text-stone-500 font-bold">
                        Override Auto-Calculated Price
                      </label>
                    </div>

                    <div className="bg-blue-50 border border-blue-100 p-3 rounded-sm text-xs text-blue-700">
                      <p className="flex items-center gap-1">
                        <Calculator size={12} />
                        Price auto-calculated based on weight and gold rate.
                      </p>
                    </div>
                  </>
                )}

                {/* For non-jewelry categories */}
                {currentProduct.category !== 'jewelry' && (
                  <div className="bg-stone-50 p-4 border border-stone-200 rounded-sm">
                    <h3 className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                      Product Details
                    </h3>
                    <p className="text-xs text-stone-400">
                      No additional specifications needed for {currentProduct.category === 'bags' ? 'bags' : currentProduct.category === 'fragrances' ? 'fragrances' : 'this category'}.
                    </p>
                  </div>
                )}

                {/* Image Upload Section */}
                <div className="border-t border-stone-100 pt-4">
                  <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                    Main Image
                  </label>
                  <div className="border-2 border-dashed border-stone-300 rounded-sm p-4">
                    {currentProduct.image ? (
                      <div className="relative group">
                        <img 
                          src={normalizeImageUrl(currentProduct.image)}
                          alt="Preview" 
                          className="w-full h-48 object-cover rounded-sm"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setCurrentProduct({ ...currentProduct, image: '' })}
                          className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm hover:text-red-500"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center justify-center h-48">
                        <Upload size={24} className="text-stone-400 mb-2" />
                        <span className="text-xs text-stone-500">Click to upload main image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleMainImageChange}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Gallery Images */}
                <div className="border-t border-stone-100 pt-4">
                  <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                    Gallery Images
                  </label>
                  <div className="border-2 border-dashed border-stone-300 rounded-sm p-4">
                    {galleryImages.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {galleryImages.map((img, index) => (
                          <div key={index} className="relative group">
                            <img 
                              src={normalizeImageUrl(img)}
                              alt={`Gallery ${index + 1}`} 
                              className="w-full h-20 object-cover rounded-sm"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveGalleryImage(index)}
                              className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow-sm hover:text-red-500"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        <label className="cursor-pointer flex items-center justify-center h-20 border border-dashed border-stone-300 rounded-sm hover:border-gold-500">
                          <Plus size={16} className="text-stone-400" />
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            multiple
                            onChange={handleGalleryUpload}
                          />
                        </label>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center justify-center h-20">
                        <Plus size={20} className="text-stone-400 mb-1" />
                        <span className="text-[10px] text-stone-500">Add gallery images</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          multiple
                          onChange={handleGalleryUpload}
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-[10px] text-stone-400 mt-2">
                    Recommended: Upload square images (1:1 ratio) for best display.
                  </p>
                </div>
              </div>

              {/* Form Actions */}
              <div className="md:col-span-2 flex justify-end gap-4 pt-6 border-t border-stone-100">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:border-stone-900 hover:text-stone-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors shadow-md disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={14} />
                      {currentProduct.id ? 'Update Product' : 'Save Product'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Promo Modal */}
        <AdminPromoModal
          isOpen={showPromoModal}
          onClose={() => setShowPromoModal(false)}
          selectedProducts={products.filter(p => selectedProducts.includes(p.id))}
          onApplyPromo={applyPromoToProducts}
        />
      </div>
    </div>
  );
};

export default AdminProducts;